// api.ts — thin fetch helpers for /api/help/*.
//
// All requests go same-origin with `credentials: 'same-origin'` so the
// JWT HttpOnly cookie rides along. No base URL — the backend serves the
// PWA, so relative paths work in dev (Vite proxy) + prod.

import type {
  FeedbackPayload,
  TopicEnvelope,
  TopicsEnvelope,
} from './types'

const BASE = '/api/help'

async function json<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers: {
      accept: 'application/json',
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  })
  if (res.status === 404) {
    const err = new Error('not_found') as Error & { status?: number }
    err.status = 404
    throw err
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    const err = new Error(
      `help api ${res.status}: ${body.slice(0, 120)}`,
    ) as Error & { status?: number }
    err.status = res.status
    throw err
  }
  return (await res.json()) as T
}

export function listTopics(params: { category?: string; query?: string } = {}) {
  const q = new URLSearchParams()
  if (params.category) q.set('category', params.category)
  if (params.query) q.set('query', params.query)
  const suffix = q.toString()
  return json<TopicsEnvelope>(`${BASE}/topics${suffix ? `?${suffix}` : ''}`)
}

export function getTopic(slug: string) {
  return json<TopicEnvelope>(
    `${BASE}/topics/${encodeURIComponent(slug)}`,
  )
}

export function submitFeedback(payload: FeedbackPayload) {
  return json<{ feedback: { id: number; created_at: string } }>(
    `${BASE}/feedback`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  )
}
